/**
 * Tagada Rx — earnings simulator.
 *
 * Affiliate: 6.9% of GMV (processing included). Standalone: 4.9%.
 * Clinician + pharmacy costs pass through at cost — never marked up.
 * No per-prescription fee.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

/** Keep in sync with `src/lib/rx/domain/pricing.ts`. */
const AFFILIATE_RATE = 0.069;
const STANDALONE_RATE = 0.049;

/** Medication + pharmacy, passed through at network cost. */
const DRUG_RATE = 0.3;
const STRIPE_RATE = 0.038;
const STRIPE_FIXED = 0.3;
const CLINICIAN_PER_RX = 20;
const DRIFT_RATE = 0.07;
const DIY_STACK_MONTHLY = 3650;

const PRESETS = [
  { key: 'hairGrowth', price: 39, patients: 2000 },
  { key: 'weightLoss', price: 250, patients: 1000 },
  { key: 'glp1', price: 299, patients: 5000 },
  { key: 'longevity', price: 280, patients: 800 },
] as const;

const usd = (n: number, digits = 0): string =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  });

const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;

type Row = {
  nameKey: string;
  taglineKey: string;
  net: number;
  highlight?: boolean;
  soon?: boolean;
};

const keepAfter = (gmv: number, deductions: number): number => Math.max(gmv - deductions, 0);

export function RxEarningsSimulator({ storeId: _storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const [price, setPrice] = React.useState(250);
  const [patients, setPatients] = React.useState(1000);

  const gmv = price * patients;
  const drug = gmv * DRUG_RATE;
  const clinicians = patients * CLINICIAN_PER_RX;
  const tagadaFee = gmv * AFFILIATE_RATE;
  const youKeep = keepAfter(gmv, tagadaFee + drug + clinicians);
  const standaloneKeep = keepAfter(gmv, gmv * STANDALONE_RATE + drug + clinicians);

  const stripe = gmv * STRIPE_RATE + patients * STRIPE_FIXED;
  const drift = gmv * DRIFT_RATE;
  const elsewhereBase = drug + clinicians + stripe + drift;

  const rows: Row[] = [
    {
      nameKey: 'earningsSimulator.rows.tagadaAffiliate.name',
      taglineKey: 'earningsSimulator.rows.tagadaAffiliate.tagline',
      net: youKeep,
      highlight: true,
    },
    {
      nameKey: 'earningsSimulator.rows.tagadaStandalone.name',
      taglineKey: 'earningsSimulator.rows.tagadaStandalone.tagline',
      net: standaloneKeep,
      soon: true,
    },
    {
      nameKey: 'earningsSimulator.rows.diy.name',
      taglineKey: 'earningsSimulator.rows.diy.tagline',
      net: keepAfter(gmv, DIY_STACK_MONTHLY + elsewhereBase),
    },
    {
      nameKey: 'earningsSimulator.rows.rimo.name',
      taglineKey: 'earningsSimulator.rows.rimo.tagline',
      net: keepAfter(gmv, 2500 + 5 * patients + elsewhereBase),
    },
    {
      nameKey: 'earningsSimulator.rows.careValidate.name',
      taglineKey: 'earningsSimulator.rows.careValidate.tagline',
      net: keepAfter(gmv, gmv * 0.1 + elsewhereBase),
    },
    {
      nameKey: 'earningsSimulator.rows.bask.name',
      taglineKey: 'earningsSimulator.rows.bask.tagline',
      net: keepAfter(gmv, gmv * 0.13 + elsewhereBase),
    },
  ];

  const liveRows = rows.filter((r) => !r.soon);
  const maxNet = Math.max(...liveRows.map((r) => r.net), 1);
  const bestRival = Math.max(...liveRows.filter((r) => !r.highlight).map((r) => r.net));
  const advantageYearly = (youKeep - bestRival) * 12;
  const youShare = youKeep / Math.max(gmv, 1);
  const feeShare = tagadaFee / Math.max(gmv, 1);
  const clinicianShare = clinicians / Math.max(gmv, 1);

  const splitRows = [
    { label: t('earningsSimulator.split.rows.youKeep'), share: youShare, highlight: true },
    { label: t('earningsSimulator.split.rows.meds'), share: DRUG_RATE },
    { label: t('earningsSimulator.split.rows.clinician'), share: clinicianShare },
    { label: t('earningsSimulator.split.rows.tagada'), share: feeShare },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4" /> {t('earningsSimulator.title')}
        </CardTitle>
        <CardDescription>
          {t('earningsSimulator.description.prefix')}
          <strong className="text-foreground font-medium">
            {t('earningsSimulator.description.strong')}
          </strong>
          {t('earningsSimulator.description.suffix')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <Button
              key={preset.key}
              variant={
                price === preset.price && patients === preset.patients ? 'default' : 'outline'
              }
              size="sm"
              onClick={() => {
                setPrice(preset.price);
                setPatients(preset.patients);
              }}
            >
              {t(`earningsSimulator.presets.${preset.key}`)}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{t('earningsSimulator.sliders.price')}</span>
              <span className="text-sm font-semibold">
                {t('earningsSimulator.sliders.priceUnit', { amount: usd(price) })}
              </span>
            </div>
            <Slider
              min={30}
              max={500}
              step={1}
              value={[price]}
              onValueChange={([v]) => setPrice(v)}
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{t('earningsSimulator.sliders.patients')}</span>
              <span className="text-sm font-semibold">{patients.toLocaleString('en-US')}</span>
            </div>
            <Slider
              min={50}
              max={10_000}
              step={50}
              value={[patients]}
              onValueChange={([v]) => setPatients(v)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-lg border bg-emerald-50/50 p-4 sm:grid-cols-3">
          <div>
            <div className="text-muted-foreground text-xs">{t('earningsSimulator.callout.monthly')}</div>
            <div className="text-2xl font-bold text-emerald-700">{usd(youKeep)}</div>
            <div className="text-muted-foreground text-xs">
              {t('earningsSimulator.callout.monthlySub', { share: pct(youShare), gmv: usd(gmv) })}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">{t('earningsSimulator.callout.yearly')}</div>
            <div className="text-2xl font-bold">{usd(youKeep * 12)}</div>
            <div className="text-muted-foreground text-xs">{t('earningsSimulator.callout.yearlySub')}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">{t('earningsSimulator.callout.takeLabel')}</div>
            <div className="text-2xl font-bold">6.9%</div>
            <div className="text-muted-foreground text-xs">
              {t('earningsSimulator.callout.takeSub', { fee: usd(tagadaFee) })}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">
            {t('earningsSimulator.split.header', { price: usd(price) })}
          </div>
          <div className="flex h-3 w-full overflow-hidden rounded-full">
            {splitRows.map((row) => (
              <div
                key={row.label}
                style={{ width: `${Math.max(row.share, 0) * 100}%` }}
                className={cn(
                  row.highlight ? 'bg-emerald-500' : 'bg-muted-foreground/30',
                  'border-background border-r last:border-r-0',
                )}
              />
            ))}
          </div>
          <ul className="text-muted-foreground space-y-1 text-xs">
            {splitRows.map((row) => (
              <li key={row.label} className="flex justify-between gap-4">
                <span className={cn(row.highlight && 'text-foreground font-medium')}>
                  {row.label}
                </span>
                <span className={cn('shrink-0', row.highlight && 'text-foreground font-semibold')}>
                  {pct(row.share)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium">{t('earningsSimulator.compare.header')}</div>
            {advantageYearly > 0 ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                <Sparkles className="mr-1 h-3 w-3" />
                {t('earningsSimulator.compare.advantage', { advantage: usd(advantageYearly) })}
              </Badge>
            ) : null}
          </div>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.nameKey} className={cn('space-y-1', row.soon && 'opacity-70')}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className={cn('font-medium', row.highlight && 'text-emerald-700')}>
                    {t(row.nameKey)}
                    {row.soon ? (
                      <span className="text-muted-foreground ml-2 text-xs font-normal">
                        {t('earningsSimulator.comingSoon')}
                      </span>
                    ) : null}
                  </span>
                  <span className={cn(row.highlight ? 'font-bold text-emerald-700' : '')}>
                    {t('earningsSimulator.compare.rowNet', { amount: usd(row.net) })}
                    <span className="text-muted-foreground ml-1 text-xs">
                      {t('earningsSimulator.compare.rowShare', {
                        share: pct(row.net / Math.max(gmv, 1)),
                      })}
                    </span>
                  </span>
                </div>
                <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      row.highlight ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                    )}
                    style={{
                      width: `${Math.max((row.net / Math.max(maxNet, row.net, 1)) * 100, 0)}%`,
                    }}
                  />
                </div>
                <div className="text-muted-foreground text-xs">{t(row.taglineKey)}</div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">{t('earningsSimulator.disclaimer')}</p>
        </div>
      </CardContent>
    </Card>
  );
}
